import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  DictationRefs,
  DictationSetters,
  UseDictationOptions,
  WebSocketHandlerParams,
  cleanupAudioRefs,
  executeStartRecording,
  executeStopRecording,
  resetAllStates,
} from "./dictation.utils";
import { useUser } from "./useUser";
import { useDocument } from "./useDocument";

function useDictationRefs(): DictationRefs {
  // useMemo with an empty dependency array ensures the same plain-object wrapper is
  // returned on every render.  Each useRef() call still returns its stable ref object;
  // wrapping them in a memoised container means closures (cleanupAudio, wsHandlerParams)
  // always reference the same DictationRefs object and never become stale.
  return useMemo<DictationRefs>(
    () => ({
      socketRef: { current: null } as React.MutableRefObject<WebSocket | null>,
      mediaRecorderRef: { current: null } as React.MutableRefObject<MediaRecorder | null>,
      audioStreamRef: { current: null } as React.MutableRefObject<MediaStream | null>,
      audioContextRef: { current: null } as React.MutableRefObject<AudioContext | null>,
      analyserRef: { current: null } as React.MutableRefObject<AnalyserNode | null>,
      audioProcessorRef: { current: null } as React.MutableRefObject<ScriptProcessorNode | null>,
      silenceTimerRef: { current: null } as React.MutableRefObject<ReturnType<
        typeof setTimeout
      > | null>,
      checkRecorderDataRef: { current: null } as React.MutableRefObject<ReturnType<
        typeof setInterval
      > | null>,
      isRecordingRef: { current: false } as React.MutableRefObject<boolean>,
      isMicroLoadingRef: { current: false } as React.MutableRefObject<boolean>,
      isSpeakingRef: { current: false } as React.MutableRefObject<boolean>,
    }),
    []
  );
}

function useSyncRefsToState(
  refs: DictationRefs,
  isSpeaking: boolean,
  isRecording: boolean,
  isMicroLoading: boolean
) {
  useEffect(() => {
    refs.isSpeakingRef.current = isSpeaking;
  }, [refs, isSpeaking]);
  useEffect(() => {
    refs.isRecordingRef.current = isRecording;
  }, [refs, isRecording]);
  useEffect(() => {
    refs.isMicroLoadingRef.current = isMicroLoading;
  }, [refs, isMicroLoading]);
}

export const useDictation = (options: UseDictationOptions = {}) => {
  const { usePCMFormat = true } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [isMicroLoading, setIsMicroLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);

  const refs: DictationRefs = useDictationRefs();
  const setters: DictationSetters = {
    setIsRecording,
    setIsMicroLoading,
    setIsConverting,
    setTranscript,
    setIsSpeaking,
  };

  useSyncRefsToState(refs, isSpeaking, isRecording, isMicroLoading);
  const { user } = useUser();
  const { data: doc } = useDocument();

  const cleanupAudio = () => cleanupAudioRefs(refs);
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);

  const wsHandlerParams: WebSocketHandlerParams = {
    options,
    usePCMFormat,
    refs,
    setters,
    user,
    doc,
    cleanupAudio,
  };

  const startRecording = () => {
    // executeStartRecording is async — catch here so a rejected promise never
    // becomes an unhandled rejection that leaves isMicroLoading stuck true.
    executeStartRecording(
      options,
      usePCMFormat,
      refs,
      setters,
      wsHandlerParams,
      cleanupAudio
    ).catch((err: unknown) => {
      console.error("[DictationDebug] Unhandled startRecording error:", err);
      cleanupAudio();
      resetAllStates(refs, setters);
    });
  };
  const stopRecording = () => executeStopRecording(usePCMFormat, refs, setters, cleanupAudio);
  const startDictation = () => {
    if (!isRecording && !isMicroLoading) {
      startRecording();
    } else {
      stopRecording();
    }
  };

  return {
    isRecording,
    isMicroLoading,
    isConverting,
    transcript,
    isSpeaking,
    startDictation,
    stopRecording,
    setTranscript,
  };
};
